import type { DocMode, RootBlockModel } from '@blocksuite/affine/model';
import { Entity } from '@toeverything/infra';
import { throttle } from 'lodash-es';
import type { Transaction } from 'yjs';

import type { DocProperties } from '../../db';
import type { WorkspaceService } from '../../workspace';
import type { DocScope } from '../scopes/doc';
import { preloadDatabaseSubdocsForPage } from '../services/database-subdoc-preload';
import type { DocsStore } from '../stores/docs';

const DOC_TRANSACTION_KEY_CLASSES = new Set([
  'blocks',
  'children',
  'displayMode',
  'index',
  'props',
  'style',
  'surface',
  'title',
  'viewport',
  'xywh',
]);

const classifyTransactionOrigin = (origin: unknown): string => {
  if (origin === null || origin === undefined) return 'none';
  if (typeof origin === 'string') return 'string';
  if (typeof origin !== 'object' && typeof origin !== 'function') {
    return typeof origin;
  }
  const constructorName = (origin as { constructor?: { name?: unknown } })
    .constructor?.name;
  return typeof constructorName === 'string' &&
    /^[A-Za-z_$][A-Za-z0-9_$-]{0,63}$/.test(constructorName)
    ? constructorName
    : 'object';
};

const markDocTransactionPerformanceSignal = (
  transaction: Transaction,
  phase: 'transaction' | 'updated-date-write'
) => {
  if (
    typeof document === 'undefined' ||
    document.documentElement.dataset.streamlainCanvasPerformanceRecording !==
      'true'
  ) {
    return;
  }

  let changedKeyCount = 0;
  let sequenceChangeCount = 0;
  let unknownKeyCount = 0;
  const keyClasses = new Set<string>();
  for (const keys of transaction.changed.values()) {
    for (const key of keys) {
      changedKeyCount += 1;
      if (key === null) {
        sequenceChangeCount += 1;
      } else if (DOC_TRANSACTION_KEY_CLASSES.has(key)) {
        keyClasses.add(key);
      } else {
        unknownKeyCount += 1;
      }
    }
  }

  const classes = [...keyClasses].sort().join('+') || 'none';
  performance.mark(
    `streamlain:doc-transaction:${phase}:local-${transaction.local ? 'yes' : 'no'}:origin-${classifyTransactionOrigin(transaction.origin)}:types-${transaction.changed.size}:parents-${transaction.changedParentTypes.size}:keys-${changedKeyCount}:sequence-${sequenceChangeCount}:unknown-${unknownKeyCount}:classes-${classes}`
  );
};

export class Doc extends Entity {
  constructor(
    public readonly scope: DocScope,
    private readonly store: DocsStore,
    private readonly workspaceService: WorkspaceService
  ) {
    super();

    const handleTransactionThrottled = throttle(
      (trx: Transaction) => {
        if (trx.local) {
          markDocTransactionPerformanceSignal(trx, 'updated-date-write');
          this.setUpdatedAt(Date.now());
        }
      },
      1000,
      {
        leading: true,
        trailing: true,
      }
    );
    const handleTransaction = (trx: Transaction) => {
      markDocTransactionPerformanceSignal(trx, 'transaction');
      handleTransactionThrottled(trx);
    };
    this.yDoc.on('afterTransaction', handleTransaction);

    this.disposables.push(() => {
      this.yDoc.off('afterTransaction', handleTransaction);
      handleTransactionThrottled.cancel();
    });

    // sl-suoi.1.3: Ensure doc is in sync queue for selective sync
    this.workspaceService.workspace.engine.doc.addDoc(this.id, 'doc-entity');
    this.disposables.push(
      this.workspaceService.workspace.engine.doc.addPriority(this.id, 100)
    );

    this.disposables.push(
      this.workspaceService.workspace.engine.indexer.addPriority(this.id, 100)
    );

    this.disposables.push(
      preloadDatabaseSubdocsForPage(this.blockSuiteDoc, this.workspaceService)
    );
  }

  /**
   * for convenience
   */
  get workspace() {
    return this.workspaceService.workspace;
  }

  get id() {
    return this.scope.props.docId;
  }

  public readonly yDoc = this.scope.props.blockSuiteDoc.spaceDoc;
  public readonly blockSuiteDoc = this.scope.props.blockSuiteDoc;
  public readonly record = this.scope.props.record;

  readonly meta$ = this.record.meta$;
  readonly properties$ = this.record.properties$;
  readonly primaryMode$ = this.record.primaryMode$;
  readonly title$ = this.record.title$;
  readonly trash$ = this.record.trash$;
  readonly createdAt$ = this.record.createdAt$;
  readonly updatedAt$ = this.record.updatedAt$;
  readonly createdBy$ = this.record.createdBy$;
  readonly updatedBy$ = this.record.updatedBy$;

  setCreatedAt(createdAt: number) {
    this.record.setMeta({ createDate: createdAt });
  }

  setUpdatedAt(updatedAt: number) {
    this.record.setMeta({ updatedDate: updatedAt });
  }

  setCreatedBy(createdBy: string) {
    this.setProperty('createdBy', createdBy);
  }

  setUpdatedBy(updatedBy: string) {
    this.setProperty('updatedBy', updatedBy);
  }

  customProperty$(propertyId: string) {
    return this.record.customProperty$(propertyId);
  }

  setProperty(propertyId: string, value: string) {
    return this.record.setProperty(propertyId, value);
  }

  updateProperties(properties: Partial<DocProperties>) {
    return this.record.updateProperties(properties);
  }

  getProperties() {
    return this.record.getProperties();
  }

  setCustomProperty(propertyId: string, value: string) {
    return this.record.setCustomProperty(propertyId, value);
  }

  setPrimaryMode(mode: DocMode) {
    return this.record.setPrimaryMode(mode);
  }

  getPrimaryMode() {
    return this.record.getPrimaryMode();
  }

  togglePrimaryMode() {
    this.setPrimaryMode(
      (this.getPrimaryMode() === 'edgeless' ? 'page' : 'edgeless') as DocMode
    );
  }

  moveToTrash() {
    return this.record.moveToTrash();
  }

  restoreFromTrash() {
    return this.record.restoreFromTrash();
  }

  waitForSyncReady() {
    return this.store.waitForDocLoadReady(this.id);
  }

  addPriorityLoad(priority: number) {
    return this.store.addPriorityLoad(this.id, priority);
  }

  changeDocTitle(newTitle: string) {
    const indexedPageBlock = this.blockSuiteDoc
      .getBlocksByFlavour('affine:page')
      .at(0)?.model as RootBlockModel | undefined;
    const rootBlock = this.blockSuiteDoc.root as RootBlockModel | null;
    const pageBlock =
      indexedPageBlock ??
      (rootBlock?.flavour === 'affine:page' ? rootBlock : undefined);
    if (pageBlock) {
      this.blockSuiteDoc.transact(() => {
        pageBlock.props.title.delete(0, pageBlock.props.title.length);
        pageBlock.props.title.insert(newTitle, 0);
      });
    }
    this.record.setMeta({ title: newTitle });
  }
}
